import { ReactElement, UIEvent, MouseEvent, useRef } from "react";
import { IComponent } from "../../../services/http/component/models/IComponent";
import NoData from "../../../components/Shared/NoData";
import { SortedField } from "../../../shared/sort";
import { DensityStates } from "../Filters";
import FixedContent from "./FixedContent";
import MainContent from "./MainContent";
import { SortUpdate, TableState } from "../table-data";
import TableWrapper, { TableColumn } from "./TableWrapper";
import { useTooltip } from "../../../hooks/UseTooltip";
import styles from "./ResultsTable.module.scss";
import { ComponentRowViewModel } from "../ComponentTableViewModel";

interface IResultsTableProps
{
    tableColumns: TableColumn[];
    tableState: TableState,
    tableLoadingState: boolean;
    setTableLoadingState: (setLoading: boolean) => void;
    selectedComponent: IComponent;
    setBreadcrumb: (componentRow: ComponentRowViewModel, full?: boolean) => ReactElement;
    setPageNumber: (page: number) => void;
    setSortData: (sortData: SortUpdate) => void;
    selectComponent: (component: IComponent) => void;
}

let lastScroll = 0;

const ResultsTable = (props: IResultsTableProps): ReactElement =>
{
    const { setupTooltip, clearTooltip, Tooltip } = useTooltip();
    const requestTimeout = useRef<number>(null as any);
    const components = props.tableState.data.rows;

    const handleHover = (e: MouseEvent): void =>
    {
        const target = e.target as HTMLElement;

        if (target && target.tagName === "TD")
        {
            const rowIndex = target.parentElement?.dataset.row;
            const rows = document.querySelectorAll("tr");
            const className = "hovering";

            for (const row of rows)
                row.dataset.row === rowIndex
                    ? row.classList.add(className)
                    : row.classList.remove(className);
        }
    }

    const handleScroll = (e: UIEvent): void =>
    {
        const target = e.currentTarget as HTMLDivElement;

        // Toggle shadow class
        if (target.scrollLeft > 0) target.classList.add(styles.withShadow);
        else target.classList.remove(styles.withShadow);

        // Load more content on scroll
        if (
            !props.tableState.data.hasNewRows() && // Checks if data isn't loading
            lastScroll < target.scrollTop && // Checks if is scrolling down
            (target.scrollTop / (target.scrollHeight - target.clientHeight) > .9) // Checks if it's near bottom
        )
        {
            clearTimeout(requestTimeout.current);

            if (!props.tableLoadingState) props.setTableLoadingState(true);
            requestTimeout.current = window.setTimeout(() =>
            {
                const increasedPageNumber = props.tableState.filters.pageNumber + 1;
                props.setPageNumber(increasedPageNumber);
            }, 500);
        }

        lastScroll = target.scrollTop;
    }

    const getSortedField = (): SortedField =>
    {
        const { sortBy, sortOrder } = props.tableState.filters;
        return { caption: sortBy, order: sortOrder };
    }

    const checkIfCollapsed = (): boolean =>
    {
        return props.tableState.configuration.density === DensityStates.Collapsed;
    }

    return components && components?.length > 0 ? (
        <div
            className={styles.resultsTable}
            onMouseMove={handleHover}
            onMouseLeave={clearTooltip}
            onScroll={handleScroll}
        >
            {/* Fixed content */}
            <TableWrapper
                tableLoadingState={props.tableLoadingState}
                columns={props.tableColumns.filter(x => x.sticky)}
                collapsed={checkIfCollapsed()}
                sortedField={getSortedField()}
                setSortData={props.setSortData}
                setTableLoadingState={props.setTableLoadingState}
                tableState={props.tableState}
            >
                <FixedContent
                    componentRows={components}
                    tableColumns={props.tableColumns}
                    collapsed={checkIfCollapsed()}
                    selectedComponent={props.selectedComponent}
                    selectComponent={props.selectComponent}
                    setBreadcrumb={props.setBreadcrumb}
                    setupTooltip={setupTooltip}
                />
            </TableWrapper>

            {/* Main content */}
            <TableWrapper
                tableLoadingState={props.tableLoadingState}
                columns={props.tableColumns.filter(x => !x.sticky)}
                collapsed={checkIfCollapsed()}
                setTableLoadingState={props.setTableLoadingState}
                tableState={props.tableState}
            >
                <MainContent
                    components={components}
                    tableColumns={props.tableColumns}
                    selectedComponent={props.selectedComponent}
                    selectComponent={props.selectComponent}
                />
            </TableWrapper>

            <Tooltip />
        </div>
    ) : (
        <NoData message="No results to display" />
    );
};

export default ResultsTable;
